// pages/HomePage.js
import React, { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
    Container, Grid, Typography, Box, Button, 
    Chip, Paper, Menu, MenuItem, IconButton, Tooltip,
    Divider, Link
} from '@mui/material';
import { motion } from 'framer-motion';
import { testimonialService } from '../services/api';
import ProductCard from '../components/ProductCard';
import TestimonialCard from '../components/TestimonialCard';
import LoadingSpinner from '../components/LoadingSpinner';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InstagramIcon from '@mui/icons-material/Instagram';
import FacebookIcon from '@mui/icons-material/Facebook';
import EmailIcon from '@mui/icons-material/Email';
import { useProducts } from '../context/ProductContext';

// ✅ Import Slick Carousel CSS
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

// ✅ WhatsApp Numbers Configuration
const WHATSAPP_NUMBERS = {
    primary: {
        number: '254714490021',
        label: 'Main Support',
        display: '+254 714 490 021'
    },
    secondary: {
        number: '+1 573 881 4830',
        label: 'Sales Team',
        display: '+1 573 881 4830'
    },
    support: {
        number: '254798765432',
        label: 'Customer Support',
        display: '+254 714 490 021'
    }
};

// ✅ Social Media Configuration
const SOCIAL_LINKS = {
    instagram: {
        url: 'https://instagram.com/uswellnesskenya',
        handle: '@uswellnesskenya',
        label: 'Instagram'
    },
    facebook: {
        url: 'https://facebook.com/uswellnesskenya',
        handle: 'US Wellness Kenya',
        label: 'Facebook'
    },
    email: {
        url: 'mailto:hello@uswellnesskenya.co.ke',
        address: 'hello@uswellnesskenya.co.ke',
        label: 'Email'
    }
};

// ✅ Banner images for the slider
const bannerImages = [
    {
        id: 1,
        src: "/static/images/products/uswellness.jpg",
        alt: "US Wellness Kenya",
        title: "AUTHENTIC • TRUSTED • WELLNESS",
        subtitle: "Premium Health Products for Your Wellness Journey"
    },
    {
        id: 2,
        src: "/static/images/products/uswellness.jpg",
        alt: "Health Products",
        title: "QUALITY SUPPLEMENTS",
        subtitle: "Discover Our Wide Range of Health Supplements"
    },
    {
        id: 3,
        src: "/static/images/products/uswellness.jpg",
        alt: "Wellness Products",
        title: "WELLNESS SOLUTIONS",
        subtitle: "Your One-Stop Shop for All Wellness Needs"
    }
];

// ✅ All products with proper slugs for navigation
const sampleProducts = [
    {
        id: 1,
        slug: "nmn-alternative-nad-liposomal",
        title: "NMN Alternative NAD+ Liposomal",
        description: "Advanced NAD+ Supplement for General Wellness Support.",
        short_description: "Advanced NAD+ supplement for cellular health",
        price: 49.99,
        discount_price: 39.99,
        image: "/static/images/products/1000450438.jpg",
        category: "Supplements",
        rating: 4.8,
        reviews: 127,
        is_new: true,
        is_bestseller: true,
        is_on_sale: true,
        discount_percentage: 20,
        stock: 45,
        brand: "NMN Alternative"
    },
    {
        id: 2,
        slug: "one-a-day-mens-multi",
        title: "ONE A DAY Men's Multi",
        description: "Complete Multivitamin for Men's Health.",
        short_description: "Complete men's multivitamin for daily health",
        price: 29.99,
        discount_price: null,
        image: "/static/images/products/1000450384.jpg",
        category: "Vitamins",
        rating: 4.6,
        reviews: 89,
        is_new: false,
        is_bestseller: true,
        is_on_sale: false,
        stock: 120,
        brand: "Bayer"
    },
    {
        id: 3,
        slug: "one-a-day-mens-multi-plus",
        title: "ONE A DAY Men's Multi Plus",
        description: "Advanced Formula with Extra Energy Support.",
        short_description: "Advanced men's multi with energy support",
        price: 34.99,
        discount_price: 27.99,
        image: "/static/images/products/1000450385.jpg",
        category: "Vitamins",
        rating: 4.7,
        reviews: 156,
        is_new: true,
        is_bestseller: false,
        is_on_sale: true,
        discount_percentage: 20,
        stock: 78,
        brand: "Bayer"
    },
    {
        id: 4,
        slug: "omega-3-fish-oil",
        title: "Omega-3 Fish Oil",
        description: "Pure Omega-3 fatty acids for heart and brain health.",
        short_description: "Pure omega-3 for heart and brain health",
        price: 24.99,
        discount_price: 19.99,
        image: "/static/images/products/1000450439.jpg",
        category: "Supplements",
        rating: 4.9,
        reviews: 203,
        is_new: false,
        is_bestseller: true,
        is_on_sale: true,
        discount_percentage: 20,
        stock: 95,
        brand: "Nature's Best"
    },
    {
        id: 5,
        slug: "vitamin-d3-5000-iu",
        title: "Vitamin D3 5000 IU",
        description: "High-potency Vitamin D3 for immune support and bone health.",
        short_description: "High-potency vitamin D3 for immune support",
        price: 19.99,
        discount_price: null,
        image: "/static/images/products/1000450394.jpg",
        category: "Vitamins",
        rating: 4.8,
        reviews: 167,
        is_new: false,
        is_bestseller: false,
        is_on_sale: false,
        stock: 150,
        brand: "Sunshine Health"
    },
    {
        id: 6,
        slug: "probiotic-50-billion-cfu",
        title: "Probiotic 50 Billion CFU",
        description: "Advanced probiotic formula for digestive health.",
        short_description: "Advanced probiotic for digestive health",
        price: 39.99,
        discount_price: 34.99,
        image: "/static/images/products/1000450432.jpg",
        category: "Wellness",
        rating: 4.7,
        reviews: 134,
        is_new: true,
        is_bestseller: false,
        is_on_sale: true,
        discount_percentage: 12,
        stock: 60,
        brand: "Gut Health Pro"
    },
    {
        id: 7,
        slug: "collagen-peptides",
        title: "Collagen Peptides",
        description: "Hydrolyzed collagen peptides for skin, hair, and joint health.",
        short_description: "Collagen for skin, hair, and joint health",
        price: 29.99,
        discount_price: null,
        image: "/static/images/products/1000450433.jpg",
        category: "Beauty",
        rating: 4.5,
        reviews: 98,
        is_new: false,
        is_bestseller: false,
        is_on_sale: false,
        stock: 85,
        brand: "Vital Proteins"
    },
    {
        id: 8,
        slug: "magnesium-glycinate",
        title: "Magnesium Glycinate",
        description: "Highly absorbable magnesium for relaxation and sleep.",
        short_description: "High-absorb magnesium for relaxation and sleep",
        price: 22.99,
        discount_price: 18.99,
        image: "/static/images/products/1000450434.jpg",
        category: "Minerals",
        rating: 4.6,
        reviews: 112,
        is_new: false,
        is_bestseller: false,
        is_on_sale: true,
        discount_percentage: 17,
        stock: 70,
        brand: "Pure Minerals"
    },
    {
        id: 9,
        slug: "coq10-200mg",
        title: "CoQ10 200mg",
        description: "Coenzyme Q10 for heart health and cellular energy.",
        short_description: "CoQ10 for heart health and energy",
        price: 34.99,
        discount_price: 29.99,
        image: "/static/images/products/1000450435.jpg",
        category: "Supplements",
        rating: 4.4,
        reviews: 76,
        is_new: true,
        is_bestseller: false,
        is_on_sale: true,
        discount_percentage: 14,
        stock: 55,
        brand: "Heart Health Plus"
    },
    {
        id: 10,
        slug: "zinc-50mg-copper",
        title: "Zinc 50mg with Copper",
        description: "Essential mineral support for immune function.",
        short_description: "Zinc with copper for immune support",
        price: 15.99,
        discount_price: null,
        image: "/static/images/products/1000450436.jpg",
        category: "Minerals",
        rating: 4.3,
        reviews: 89,
        is_new: false,
        is_bestseller: false,
        is_on_sale: false,
        stock: 110,
        brand: "Essential Minerals"
    },
    {
        id: 11,
        slug: "turmeric-curcumin-complex",
        title: "Turmeric Curcumin Complex",
        description: "Premium turmeric extract with black pepper.",
        short_description: "Turmeric complex for joint health",
        price: 27.99,
        discount_price: 22.99,
        image: "/static/images/products/1000450437.jpg",
        category: "Wellness",
        rating: 4.8,
        reviews: 189,
        is_new: false,
        is_bestseller: true,
        is_on_sale: true,
        discount_percentage: 18,
        stock: 92,
        brand: "Golden Wellness"
    },
    {
        id: 12,
        slug: "b-complex-complete",
        title: "B-Complex Complete",
        description: "Complete B-vitamin complex for energy and vitality.",
        short_description: "Complete B-vitamins for energy and vitality",
        price: 18.99,
        discount_price: null,
        image: "/static/images/products/1000450388.jpg",
        category: "Vitamins",
        rating: 4.5,
        reviews: 103,
        is_new: true,
        is_bestseller: false,
        is_on_sale: false,
        stock: 130,
        brand: "Energy Boost"
    }
];

function HomePage() {
    const [activeTab, setActiveTab] = useState('all products');
    const [whatsappAnchorEl, setWhatsappAnchorEl] = useState(null);
    const [selectedWhatsApp, setSelectedWhatsApp] = useState(WHATSAPP_NUMBERS.primary);
    
    const { products: contextProducts, loading: contextLoading, refreshProducts } = useProducts();

    const { data: testimonials } = useQuery({
        queryKey: ['testimonials'],
        queryFn: testimonialService.getLatestTestimonials,
        retry: false,
    });

    useEffect(() => {
        if (refreshProducts) {
            refreshProducts();
        }
    }, [refreshProducts]);

    // ✅ Handle WhatsApp menu
    const handleWhatsAppClick = (event) => {
        setWhatsappAnchorEl(event.currentTarget);
    };

    const handleWhatsAppClose = () => {
        setWhatsappAnchorEl(null);
    };

    const handleWhatsAppSelect = (numberKey) => {
        setSelectedWhatsApp(WHATSAPP_NUMBERS[numberKey]);
        handleWhatsAppClose();
        // Open WhatsApp with selected number
        const message = "Hello, I would like to inquire about your products.";
        window.open(`https://wa.me/${WHATSAPP_NUMBERS[numberKey].number}?text=${encodeURIComponent(message)}`, '_blank');
    };

    // ✅ Move useMemo BEFORE any conditional returns
    const displayProducts = useMemo(() => {
        if (contextProducts && contextProducts.length > 0) {
            return contextProducts.map(product => ({
                ...product,
                slug: product.slug || product.title?.toLowerCase().replace(/[^a-z0-9]+/g, '-') || `product-${product.id}`,
                short_description: product.short_description || product.description?.substring(0, 100) || '',
                rating: product.rating || 4.0,
                reviews: product.reviews || 0,
                is_new: product.is_new || false,
                is_bestseller: product.is_bestseller || false,
                is_on_sale: product.is_on_sale || false,
                stock: product.stock || 0,
                brand: product.brand || '',
                category: product.category || 'Uncategorized'
            }));
        }
        return sampleProducts;
    }, [contextProducts]);

    // ✅ Filter products based on active tab
    const filteredProducts = useMemo(() => {
        switch(activeTab) {
            case 'supplements':
                return displayProducts.filter(p => p.category?.toLowerCase() === 'supplements');
            case 'vitamins':
                return displayProducts.filter(p => p.category?.toLowerCase() === 'vitamins');
            case 'wellness':
                return displayProducts.filter(p => p.category?.toLowerCase() === 'wellness');
            case 'new arrivals':
                return displayProducts.filter(p => p.is_new === true);
            case 'best sellers':
                return displayProducts.filter(p => p.is_bestseller === true);
            case 'on sale':
                return displayProducts.filter(p => p.is_on_sale === true);
            default:
                return displayProducts;
        }
    }, [activeTab, displayProducts]);

    // ✅ Now we can have conditional returns after all hooks
    if (contextLoading && displayProducts.length === 0) {
        return <LoadingSpinner />;
    }

    // ✅ Slick Slider Settings
    const sliderSettings = {
        dots: true,
        infinite: true,
        speed: 500,
        slidesToShow: 1,
        slidesToScroll: 1,
        autoplay: true,
        autoplaySpeed: 5000,
        pauseOnHover: true,
        arrows: true,
        fade: true,
        cssEase: 'linear',
        responsive: [
            {
                breakpoint: 768,
                settings: {
                    arrows: false,
                    dots: true
                }
            }
        ]
    };

    return (
        <Box sx={{ bgcolor: '#f8f9fa' }}>
            {/* ✅ SLIDING BANNER CAROUSEL */}
            <Box sx={{ 
                width: '100%',
                position: 'relative',
                borderBottom: '3px solid #667eea',
                overflow: 'hidden',
            }}>
                <Slider {...sliderSettings}>
                    {bannerImages.map((banner) => (
                        <Box key={banner.id}>
                            <Box
                                sx={{
                                    width: '100%',
                                    height: { xs: '200px', sm: '280px', md: '380px' },
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: '#e8f0fe',
                                    overflow: 'hidden',
                                    position: 'relative',
                                }}
                            >
                                <Box
                                    component="img"
                                    src={banner.src}
                                    alt={banner.alt}
                                    sx={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                        display: 'block',
                                    }}
                                />
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        bottom: { xs: 12, sm: 20 },
                                        left: { xs: 16, sm: 32 },
                                        color: 'white',
                                        textShadow: '0 2px 8px rgba(0,0,0,0.4)'
                                    }}
                                >
                                    <Typography variant="caption" sx={{ 
                                        bgcolor: 'rgba(102,126,234,0.9)',
                                        px: 2.5,
                                        py: 0.75,
                                        borderRadius: 2,
                                        fontWeight: 'bold',
                                        fontSize: { xs: '0.65rem', sm: '0.85rem' },
                                        backdropFilter: 'blur(4px)',
                                        display: 'inline-block'
                                    }}>
                                        {banner.title}
                                    </Typography>
                                    {banner.subtitle && (
                                        <Typography 
                                            variant="body2" 
                                            sx={{ 
                                                mt: 1,
                                                color: 'white',
                                                fontWeight: 500,
                                                fontSize: { xs: '0.7rem', sm: '0.9rem' },
                                                textShadow: '0 2px 4px rgba(0,0,0,0.5)',
                                                display: { xs: 'none', sm: 'block' }
                                            }}
                                        >
                                            {banner.subtitle}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        </Box>
                    ))}
                </Slider>
            </Box>

            {/* ✅ SOCIAL MEDIA & CONTACT BAR */}
            <Box sx={{ 
                bgcolor: 'white', 
                borderBottom: '1px solid #e0e0e0',
                py: 1.5
            }}>
                <Container maxWidth="lg">
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: 1.5
                    }}>
                        {/* Social Media Links */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                Follow Us:
                            </Typography>
                            <Tooltip title="Follow us on Instagram">
                                <IconButton
                                    component="a"
                                    href={SOCIAL_LINKS.instagram.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{ 
                                        color: '#E4405F',
                                        '&:hover': { 
                                            bgcolor: '#E4405F10',
                                            transform: 'scale(1.1)'
                                        },
                                        transition: 'all 0.2s'
                                    }}
                                >
                                    <InstagramIcon />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Follow us on Facebook">
                                <IconButton
                                    component="a"
                                    href={SOCIAL_LINKS.facebook.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{ 
                                        color: '#1877F2',
                                        '&:hover': { 
                                            bgcolor: '#1877F210',
                                            transform: 'scale(1.1)'
                                        },
                                        transition: 'all 0.2s'
                                    }}
                                >
                                    <FacebookIcon />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Email us">
                                <IconButton
                                    component="a"
                                    href={SOCIAL_LINKS.email.url}
                                    sx={{ 
                                        color: '#EA4335',
                                        '&:hover': { 
                                            bgcolor: '#EA433510',
                                            transform: 'scale(1.1)'
                                        },
                                        transition: 'all 0.2s'
                                    }}
                                >
                                    <EmailIcon />
                                </IconButton>
                            </Tooltip>
                        </Box>

                        {/* Social Handles & Email */}
                        <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 2,
                            flexWrap: 'wrap'
                        }}>
                            <Link
                                href={SOCIAL_LINKS.instagram.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                underline="none"
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    fontSize: '0.8rem',
                                    color: '#555',
                                    '&:hover': { color: '#E4405F' }
                                }}
                            >
                                <InstagramIcon sx={{ fontSize: 16 }} />
                                {SOCIAL_LINKS.instagram.handle}
                            </Link>
                            
                            <Divider orientation="vertical" flexItem sx={{ height: 20 }} />
                            
                            <Link
                                href={SOCIAL_LINKS.facebook.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                underline="none"
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    fontSize: '0.8rem',
                                    color: '#555',
                                    '&:hover': { color: '#1877F2' }
                                }}
                            >
                                <FacebookIcon sx={{ fontSize: 16 }} />
                                {SOCIAL_LINKS.facebook.handle}
                            </Link>
                            
                            <Divider orientation="vertical" flexItem sx={{ height: 20 }} />
                            
                            <Link
                                href={SOCIAL_LINKS.email.url}
                                underline="none"
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    fontSize: '0.8rem',
                                    color: '#555',
                                    '&:hover': { color: '#EA4335' }
                                }}
                            >
                                <EmailIcon sx={{ fontSize: 16 }} />
                                {SOCIAL_LINKS.email.address}
                            </Link>
                        </Box>
                    </Box>
                </Container>
            </Box>

            {/* ✅ CATEGORY TABS - Contained and centered */}
            <Container maxWidth="lg" sx={{ mb: 1.5, mt: 2 }}>
                <Box sx={{ 
                    display: 'flex', 
                    gap: 1, 
                    overflowX: 'auto', 
                    pb: 1.5,
                    '&::-webkit-scrollbar': { height: 4 },
                    '&::-webkit-scrollbar-thumb': { bgcolor: '#667eea', borderRadius: 10 }
                }}>
                    {['All Products', 'Supplements', 'Vitamins', 'Wellness', 'New Arrivals', 'Best Sellers', 'On Sale'].map((tab) => (
                        <Chip
                            key={tab}
                            label={tab}
                            onClick={() => setActiveTab(tab.toLowerCase())}
                            size="small"
                            sx={{
                                bgcolor: activeTab === tab.toLowerCase() ? '#667eea' : 'white',
                                color: activeTab === tab.toLowerCase() ? 'white' : '#333',
                                fontWeight: activeTab === tab.toLowerCase() ? 'bold' : '500',
                                fontSize: '0.78rem',
                                py: 0.5,
                                px: 0.5,
                                cursor: 'pointer',
                                border: '1px solid',
                                borderColor: activeTab === tab.toLowerCase() ? '#667eea' : '#e0e0e0',
                                flexShrink: 0,
                                '&:hover': {
                                    bgcolor: activeTab === tab.toLowerCase() ? '#667eea' : '#f0f0f0',
                                    transform: 'translateY(-2px)',
                                    transition: 'all 0.2s'
                                },
                                transition: 'all 0.2s',
                                height: 32,
                            }}
                        />
                    ))}
                </Box>
            </Container>

            {/* ✅ ORDER VIA WHATSAPP - Multiple Numbers */}
            <Container maxWidth="lg" sx={{ mb: 2 }}>
                <Paper
                    elevation={2}
                    sx={{
                        p: { xs: 1.5, sm: 2 },
                        background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: 1.5
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <WhatsAppIcon sx={{ color: 'white', fontSize: { xs: 24, sm: 32 } }} />
                        <Box>
                            <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 'bold', fontSize: { xs: '0.9rem', sm: '1.1rem' } }}>
                                Order via WhatsApp
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.9)', display: { xs: 'none', sm: 'block' } }}>
                                {selectedWhatsApp.label}: {selectedWhatsApp.display}
                            </Typography>
                        </Box>
                    </Box>
                    
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {/* Main WhatsApp Button */}
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={<WhatsAppIcon />}
                            sx={{
                                bgcolor: 'white',
                                color: '#25D366',
                                '&:hover': { 
                                    bgcolor: '#f0f0f0',
                                    transform: 'scale(1.05)'
                                },
                                px: 3,
                                py: 0.75,
                                borderRadius: 50,
                                fontWeight: 'bold',
                                fontSize: { xs: '0.7rem', sm: '0.85rem' },
                                transition: 'all 0.3s'
                            }}
                            href={`https://wa.me/${selectedWhatsApp.number}?text=${encodeURIComponent('Hello, I would like to inquire about your products.')}`}
                            target="_blank"
                        >
                            CHAT NOW
                        </Button>

                        {/* Dropdown for multiple numbers */}
                        <Button
                            variant="contained"
                            size="small"
                            endIcon={<ExpandMoreIcon />}
                            onClick={handleWhatsAppClick}
                            sx={{
                                bgcolor: 'rgba(255,255,255,0.2)',
                                color: 'white',
                                '&:hover': { 
                                    bgcolor: 'rgba(255,255,255,0.3)',
                                },
                                px: 2,
                                py: 0.75,
                                borderRadius: 50,
                                fontWeight: 'bold',
                                fontSize: { xs: '0.7rem', sm: '0.85rem' },
                                transition: 'all 0.3s',
                                minWidth: 'auto'
                            }}
                        >
                            More
                        </Button>

                        {/* WhatsApp Number Menu */}
                        <Menu
                            anchorEl={whatsappAnchorEl}
                            open={Boolean(whatsappAnchorEl)}
                            onClose={handleWhatsAppClose}
                            PaperProps={{
                                sx: {
                                    mt: 1,
                                    borderRadius: 2,
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
                                }
                            }}
                        >
                            {Object.entries(WHATSAPP_NUMBERS).map(([key, value]) => (
                                <MenuItem 
                                    key={key}
                                    onClick={() => handleWhatsAppSelect(key)}
                                    sx={{
                                        py: 1.5,
                                        px: 3,
                                        gap: 2,
                                        '&:hover': {
                                            bgcolor: '#25D36610'
                                        },
                                        ...(selectedWhatsApp.number === value.number && {
                                            bgcolor: '#25D36615',
                                            borderLeft: '3px solid #25D366'
                                        })
                                    }}
                                >
                                    <WhatsAppIcon sx={{ color: '#25D366', fontSize: 20 }} />
                                    <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {value.label}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {value.display}
                                        </Typography>
                                    </Box>
                                    {selectedWhatsApp.number === value.number && (
                                        <Box sx={{ 
                                            ml: 'auto', 
                                            color: '#25D366',
                                            fontSize: '12px'
                                        }}>
                                            ✓
                                        </Box>
                                    )}
                                </MenuItem>
                            ))}
                        </Menu>
                    </Box>
                </Paper>
            </Container>

            {/* ✅ PRODUCTS GRID - 4 cards per row on large screens */}
            <Container maxWidth="lg" sx={{ py: 1 }}>
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center', 
                    mb: 2.5,
                    borderBottom: '2px solid #e9ecef',
                    pb: 1
                }}>
                    <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', color: '#333', fontSize: { xs: '1.1rem', sm: '1.3rem' } }}>
                        {activeTab === 'all products' && `All Products (${filteredProducts.length})`}
                        {activeTab === 'supplements' && `Supplements (${filteredProducts.length})`}
                        {activeTab === 'vitamins' && `Vitamins (${filteredProducts.length})`}
                        {activeTab === 'new arrivals' && `New Arrivals (${filteredProducts.length})`}
                        {activeTab === 'best sellers' && `Best Sellers (${filteredProducts.length})`}
                        {activeTab === 'on sale' && `On Sale (${filteredProducts.length})`}
                        {activeTab === 'wellness' && `Wellness (${filteredProducts.length})`}
                    </Typography>
                    <Button 
                        color="primary" 
                        endIcon={<TrendingUpIcon />}
                        size="small"
                        sx={{ 
                            fontWeight: 'bold',
                            fontSize: '0.8rem',
                            textTransform: 'none'
                        }}
                    >
                        View All
                    </Button>
                </Box>

                <Grid container spacing={2}>
                    {filteredProducts.map((product, index) => (
                        <Grid 
                            item 
                            key={product.id} 
                            xs={6}        
                            sm={6}        
                            md={4}        
                            lg={3}        
                            xl={3}        
                        >
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.4, delay: Math.min(index * 0.05, 0.5) }}
                                style={{ height: '100%' }}
                            >
                                <ProductCard product={product} index={index} />
                            </motion.div>
                        </Grid>
                    ))}
                </Grid>

                {filteredProducts.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 6 }}>
                        <Typography variant="h6" color="text.secondary">
                            No products found in this category
                        </Typography>
                    </Box>
                )}
            </Container>

            {/* ✅ SHOP BY CATEGORY - Contained and centered */}
            <Box sx={{ bgcolor: 'white', py: 4, mt: 3 }}>
                <Container maxWidth="lg">
                    <Typography variant="h5" component="h2" align="center" sx={{ mb: 3, fontWeight: 'bold' }}>
                        Shop by Category
                    </Typography>
                    <Grid container spacing={2}>
                        {[
                            { name: 'Supplements', icon: '💪', color: '#FF6B6B', count: '45+' },
                            { name: 'Vitamins', icon: '✨', color: '#4ECDC4', count: '32+' },
                            { name: 'Wellness', icon: '🌿', color: '#45B7D1', count: '28+' },
                            { name: 'New Arrivals', icon: '🌟', color: '#96CEB4', count: '15+' },
                            { name: 'Best Sellers', icon: '🔥', color: '#FF9F43', count: '20+' },
                            { name: 'On Sale', icon: '💰', color: '#A29BFE', count: '12+' }
                        ].map((category) => (
                            <Grid item xs={6} sm={4} md={2} key={category.name}>
                                <motion.div
                                    whileHover={{ scale: 1.05, y: -3 }}
                                    transition={{ duration: 0.2 }}
                                    onClick={() => setActiveTab(category.name.toLowerCase())}
                                >
                                    <Paper
                                        elevation={1}
                                        sx={{
                                            p: 2.5,
                                            textAlign: 'center',
                                            cursor: 'pointer',
                                            bgcolor: `${category.color}10`,
                                            border: `2px solid ${category.color}25`,
                                            transition: 'all 0.3s',
                                            borderRadius: 2,
                                            '&:hover': {
                                                boxShadow: 4,
                                                bgcolor: `${category.color}20`,
                                            }
                                        }}
                                    >
                                        <Typography variant="h3" sx={{ fontSize: { xs: '2rem', sm: '2.5rem' } }}>
                                            {category.icon}
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 0.5 }}>
                                            {category.name}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {category.count} products
                                        </Typography>
                                    </Paper>
                                </motion.div>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* ✅ TESTIMONIALS - Contained and centered */}
            {testimonials && testimonials.length > 0 && (
                <Box sx={{ bgcolor: '#f8f9fa', py: 4 }}>
                    <Container maxWidth="lg">
                        <Typography variant="h5" component="h2" align="center" sx={{ mb: 3, fontWeight: 'bold' }}>
                            What Our Customers Say
                        </Typography>
                        <Grid container spacing={3}>
                            {testimonials.slice(0, 3).map((testimonial, index) => (
                                <Grid item key={testimonial.id} xs={12} md={4}>
                                    <motion.div
                                        initial={{ opacity: 0, scale: 0.95 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        transition={{ duration: 0.4, delay: index * 0.1 }}
                                    >
                                        <TestimonialCard testimonial={testimonial} />
                                    </motion.div>
                                </Grid>
                            ))}
                        </Grid>
                    </Container>
                </Box>
            )}

            {/* ✅ NEWSLETTER & SOCIAL FOOTER - Contained and centered */}
            <Box
                sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    py: 4,
                    mt: 3,
                    width: '100%'
                }}
            >
                <Container maxWidth="lg">
                    {/* Newsletter Section */}
                    <Box sx={{ maxWidth: 'md', mx: 'auto', mb: 3 }}>
                        <Typography variant="h5" align="center" sx={{ fontWeight: 'bold', fontSize: { xs: '1.1rem', sm: '1.3rem' } }}>
                            📧 Subscribe to Our Newsletter
                        </Typography>
                        <Typography variant="body2" align="center" sx={{ mb: 2.5, opacity: 0.9 }}>
                            Get the latest updates on new products and special offers
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap' }}>
                            <input
                                type="email"
                                placeholder="Enter your email"
                                style={{
                                    padding: '10px 18px',
                                    borderRadius: '8px',
                                    border: 'none',
                                    width: '280px',
                                    fontSize: '14px',
                                    outline: 'none'
                                }}
                            />
                            <Button 
                                variant="contained" 
                                sx={{ 
                                    bgcolor: 'white', 
                                    color: '#667eea',
                                    fontWeight: 'bold',
                                    '&:hover': { bgcolor: '#f0f0f0' },
                                    px: 4
                                }}
                            >
                                Subscribe
                            </Button>
                        </Box>
                    </Box>

                    {/* Divider */}
                    <Divider sx={{ bgcolor: 'rgba(255,255,255,0.2)', my: 3 }} />

                    {/* Social & Contact Footer */}
                    <Box sx={{ 
                        display: 'flex', 
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                        textAlign: { xs: 'center', sm: 'left' }
                    }}>
                        {/* Social Links */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Typography variant="body2" sx={{ opacity: 0.8 }}>
                                Connect with us:
                            </Typography>
                            <IconButton
                                component="a"
                                href={SOCIAL_LINKS.instagram.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ 
                                    color: 'white',
                                    '&:hover': { 
                                        bgcolor: 'rgba(255,255,255,0.15)',
                                        transform: 'scale(1.1)'
                                    },
                                    transition: 'all 0.2s'
                                }}
                            >
                                <InstagramIcon />
                            </IconButton>
                            <IconButton
                                component="a"
                                href={SOCIAL_LINKS.facebook.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ 
                                    color: 'white',
                                    '&:hover': { 
                                        bgcolor: 'rgba(255,255,255,0.15)',
                                        transform: 'scale(1.1)'
                                    },
                                    transition: 'all 0.2s'
                                }}
                            >
                                <FacebookIcon />
                            </IconButton>
                            <IconButton
                                component="a"
                                href={SOCIAL_LINKS.email.url}
                                sx={{ 
                                    color: 'white',
                                    '&:hover': { 
                                        bgcolor: 'rgba(255,255,255,0.15)',
                                        transform: 'scale(1.1)'
                                    },
                                    transition: 'all 0.2s'
                                }}
                            >
                                <EmailIcon />
                            </IconButton>
                        </Box>

                        {/* Contact Info */}
                        <Box sx={{ 
                            display: 'flex', 
                            flexDirection: { xs: 'column', sm: 'row' },
                            alignItems: 'center',
                            gap: { xs: 1, sm: 3 }
                        }}>
                            <Typography variant="body2" sx={{ opacity: 0.9, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                📸 <Link href={SOCIAL_LINKS.instagram.url} target="_blank" rel="noopener noreferrer" sx={{ color: 'white', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                                    {SOCIAL_LINKS.instagram.handle}
                                </Link>
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.9, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                👍 <Link href={SOCIAL_LINKS.facebook.url} target="_blank" rel="noopener noreferrer" sx={{ color: 'white', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                                    {SOCIAL_LINKS.facebook.handle}
                                </Link>
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.9, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                ✉️ <Link href={SOCIAL_LINKS.email.url} sx={{ color: 'white', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                                    {SOCIAL_LINKS.email.address}
                                </Link>
                            </Typography>
                        </Box>
                    </Box>

                    {/* Copyright */}
                    <Typography variant="caption" sx={{ 
                        display: 'block', 
                        textAlign: 'center', 
                        mt: 3, 
                        opacity: 0.6,
                        fontSize: '0.7rem'
                    }}>
                        © {new Date().getFullYear()} US Wellness Kenya. All rights reserved.
                    </Typography>
                </Container>
            </Box>
        </Box>
    );
}

export default HomePage;